import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Save, Upload, FileText, Plus, Edit2, Trash2, X, Calendar } from 'lucide-react';
import PDFStylingEditor from '@/components/PDFStylingEditor';
import TermsOfTradeEditor from '@/components/TermsOfTradeEditor';
import { QuoteTermsDefault } from '@/components/QuoteTermsDefault';
import InstantReportScriptsEditor from '@/components/InstantReportScriptsEditor';

const ALL_SECTIONS = [
  { key: 'intro', label: 'Introduction Text' },
  { key: 'roof_details', label: 'Roof Details Grid' },
  { key: 'conditions', label: 'Condition Assessment' },
  { key: 'components_summary', label: 'Components Summary' },
  { key: 'repair_options', label: 'Repair Options List' },
  { key: 'restoration_steps', label: 'Restoration Process Steps' },
  { key: 'ridging_info', label: 'Ridging Information' },
  { key: 'estimates_area', label: 'Area-Based Estimates' },
  { key: 'quote_items_summary', label: 'Quote Items & Totals' },
  { key: 'extra_works', label: 'Extra Works' },
  { key: 'discount_note', label: 'Discount Note' },
];

export default function ReportSetupPage() {
  const { api } = useAuth();
  const location = useLocation();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Seed the active tab from the URL `?tab=` query param so sidebar
  // shortcuts (e.g. /report-setup?tab=pdf_styling) land on the right view.
  const [tab, setTab] = useState(() => new URLSearchParams(location.search).get('tab') || 'general');

  // Keep the tab in sync if the user navigates between sidebar shortcuts
  // without unmounting the page.
  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('tab');
    if (requested && requested !== tab) setTab(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [gstRates, setGstRates] = useState([]);
  const [newGst, setNewGst] = useState({ rate: '', effective_from: '', description: '' });
  const [earliestDate, setEarliestDate] = useState('');

  const load = useCallback(async () => {
    try {
      const [settingsRes, templatesRes, gstRes, earliestRes] = await Promise.all([
        api.get('/report-settings'),
        api.get('/report-templates'),
        api.get('/gst-rates'),
        api.get('/report-settings/earliest-date'),
      ]);
      setForm(settingsRes.data);
      setTemplates(templatesRes.data);
      setGstRates(gstRes.data || []);
      setEarliestDate(earliestRes.data?.earliest_date || '');
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/report-settings', form);
      setForm(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert('Failed to save'); }
    setSaving(false);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/report-settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, logo_url: data.logo_url }));
    } catch (err) { alert('Logo upload failed'); }
  };

  const saveTemplate = async (t) => {
    try {
      if (t._id) {
        await api.put(`/report-templates/${t._id}`, t);
      } else {
        await api.post('/report-templates', t);
      }
      setEditTemplate(null);
      const { data } = await api.get('/report-templates');
      setTemplates(data);
    } catch (e) { alert('Failed to save template'); }
  };

  const deleteTemplate = async (id) => {
    await api.delete(`/report-templates/${id}`);
    const { data } = await api.get('/report-templates');
    setTemplates(data);
  };

  const addGstRate = async () => {
    if (!newGst.rate || !newGst.effective_from) return;
    try {
      const { data } = await api.post('/gst-rates', newGst);
      setGstRates(data);
      setNewGst({ rate: '', effective_from: '', description: '' });
    } catch (e) { alert('Failed to add GST rate'); }
  };

  const deleteGstRate = async (id) => {
    try {
      await api.delete(`/gst-rates/${id}`);
      const { data } = await api.get('/gst-rates');
      setGstRates(data);
    } catch (e) { alert('Failed to delete'); }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const Field = ({ label, field, rows }) => (
    <div>
      <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</label>
      {rows ? (
        <textarea value={form[field] || ''} onChange={e => u(field, e.target.value)} rows={rows} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono" />
      ) : (
        <input value={form[field] || ''} onChange={e => u(field, e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
      )}
    </div>
  );

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;

  const TABS = [
    { key: 'general', label: 'Company & Logo' },
    { key: 'pdf_styling', label: 'PDF Styling' },
    { key: 'terms_of_trade', label: 'Terms of Trade' },
    { key: 'instant_report', label: 'Report Scripts' },
    { key: 'section_spacing', label: 'Section Spacing' },
    { key: 'roof_rates', label: 'Roof Rates' },
    // Defect Types editor deprecated 2026-02 — data consolidated into
    // condition_scripts. Use Settings → Config → Roof Conditions instead
    // (single source of truth for all 11 categories including Ridging,
    // Tiles, Valleys, Barging, Sheeting, Spouting, Fastenings, Flashings,
    // Roof Condition, Roof Recommendation, Appraisal Condition).
    { key: 'intros', label: 'Introduction Text' },
    { key: 'restoration', label: 'Restoration Process' },
    { key: 'repairs', label: 'Repair Options' },
    { key: 'other', label: 'Other Text' },
    { key: 'templates', label: `Templates (${templates.length})` },
    { key: 'gst', label: 'GST Settings' },
    { key: 'invoice', label: 'Invoice Settings' },
  ];

  return (
    <div data-testid="report-setup-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2C2C2C]">Report Setup</h1>
          <p className="text-sm text-slate-500 mt-1">Configure branding, text, and per-type templates for all PDF reports.</p>
        </div>
        {tab !== 'templates' && tab !== 'gst' && tab !== 'invoice' && tab !== 'pdf_styling' && tab !== 'terms_of_trade' && tab !== 'instant_report' && tab !== 'section_spacing' && tab !== 'roof_rates' && (
          <button data-testid="save-report-settings" onClick={save} disabled={saving} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] hover:bg-[#A04000] text-white'} disabled:opacity-50`}>
            <Save size={14} /> {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
          </button>
        )}
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} data-testid={`report-setup-tab-${t.key}`} onClick={() => setTab(t.key)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${tab === t.key ? 'bg-white text-[#D35400] shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-xl p-6 space-y-5">
        {tab === 'pdf_styling' && <PDFStylingEditor />}
        {tab === 'terms_of_trade' && <><QuoteTermsDefault /><TermsOfTradeEditor /></>}
        {tab === 'instant_report' && <InstantReportScriptsEditor />}
        {tab === 'section_spacing' && <SectionSpacingEditor form={form} u={u} save={save} saving={saving} saved={saved} />}
        {tab === 'roof_rates' && <RoofRatesEditor api={api} />}
        {/* defect_types tab retired 2026-02 — see Roof Conditions Config */}
        {tab === 'general' && (
          <div className="space-y-5">
            {/* CRM Start Date */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Calendar size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-slate-800 mb-1">CRM Start Date</h4>
                  <p className="text-xs text-slate-500 mb-3">All appraisals, invoices, expenses, and financial reports before this date will be ignored. This gives you a clean starting point — reference data (clients, properties, suppliers, products, dropdowns) is not affected.</p>
                  <div className="flex items-center gap-3">
                    <input
                      data-testid="crm-start-date"
                      type="date"
                      value={form.crm_start_date || ''}
                      onChange={e => u('crm_start_date', e.target.value)}
                      className="px-3 py-2 border border-amber-300 rounded-md text-sm bg-white"
                    />
                    {form.crm_start_date && (
                      <button
                        data-testid="clear-crm-start-date"
                        onClick={() => u('crm_start_date', '')}
                        className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                      >Clear (show all data)</button>
                    )}
                    {earliestDate && (
                      <span className="text-[10px] text-slate-400">Earliest data in CRM: {earliestDate}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
            <div className="flex-shrink-0">
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Company Logo</label>
              <div className="w-48 h-24 border-2 border-dashed border-[#E2E2DF] rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                {form.logo_url ? <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" /> : <FileText size={24} className="text-slate-300" />}
              </div>
              <label className="flex items-center gap-1.5 mt-2 text-xs text-[#D35400] font-semibold cursor-pointer hover:text-[#A04000]">
                <Upload size={12} /> Upload Logo
                <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
              </label>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
              <Field label="Company Name" field="company_name" />
              <Field label="Website" field="website" />
              <Field label="Email" field="email" />
              <Field label="Phone" field="phone" />
              <Field label="Representative" field="representative" />
              <Field label="After Hours Phone" field="after_hours_phone" />
              <Field label="Tagline" field="tagline" />
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Default Materials Margin %</label>
                <input type="number" step="0.1" value={form.default_materials_margin ?? 5} onChange={e => u('default_materials_margin', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                <p className="text-[10px] text-slate-400 mt-1">Used when products don't have catalog buy cost rates. Applied as: Buy Cost = Materials × (1 - margin%/100)</p>
              </div>
            </div>
          </div>
          </div>
        )}

        {tab === 'intros' && (
          <div className="space-y-4">
            <p className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg">Placeholders: {'{property_address}'}, {'{roof_type}'}, {'{roof_brand}'}, {'{client_name}'}</p>
            <Field label="Appraisal Introduction" field="intro_appraisal" rows={4} />
            <Field label="Repairs Introduction" field="intro_repairs" rows={4} />
            <Field label="Restoration Introduction" field="intro_restoration" rows={4} />
          </div>
        )}

        {tab === 'restoration' && (
          <div className="space-y-4">
            <Field label="Concrete Tile Restoration Steps" field="restoration_steps_concrete" rows={8} />
            <Field label="Iron Roof Restoration Steps" field="restoration_steps_iron" rows={6} />
          </div>
        )}

        {tab === 'repairs' && (
          <div className="space-y-4">
            <Field label="Concrete Tile Repair Options" field="repair_options_concrete" rows={8} />
            <Field label="Iron Roof Repair Options" field="repair_options_iron" rows={6} />
          </div>
        )}

        {tab === 'other' && (
          <div className="space-y-4">
            <Field label="Ridging Information" field="ridging_info" rows={4} />
            <Field label="Extra Works Text" field="extra_works_text" rows={3} />
            <Field label="Re-Roof Note" field="reroof_note" rows={2} />
            <Field label="Discount Note" field="discount_note" rows={2} />
            <Field label="Footer Text" field="footer_text" rows={2} />
          </div>
        )}

        {tab === 'templates' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">Each quote type can have its own report template controlling which sections appear in the Appraisal PDF.</p>
              <button data-testid="add-template-btn" onClick={() => setEditTemplate({ quote_type: '', sections: ['intro', 'roof_details', 'conditions', 'components_summary', 'quote_items_summary'], intro_override: '', use_quote_item_totals: true, show_restoration_estimate: true, show_reroof_estimate: true })} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000]"><Plus size={14} /> Add Template</button>
            </div>

            {editTemplate && (
              <TemplateEditor template={editTemplate} onSave={saveTemplate} onCancel={() => setEditTemplate(null)} />
            )}

            <div className="space-y-2">
              {templates.map(t => (
                <div key={t._id} className="flex items-center justify-between p-3 border border-[#E2E2DF] rounded-lg hover:border-slate-300">
                  <div>
                    <span className="text-sm font-semibold text-slate-800">{t.quote_type === '_default' ? 'Default (fallback)' : t.quote_type}</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(t.sections || []).map(s => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{s}</span>)}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-template-${t._id}`} onClick={() => setEditTemplate({...t})} className="p-1 text-slate-400 hover:text-[#D35400]"><Edit2 size={14} /></button>
                    {t.quote_type !== '_default' && <button onClick={() => deleteTemplate(t._id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'invoice' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">Invoice Settings</h3>
              <p className="text-sm text-slate-500">Payment details and terms that appear on generated invoices. Changes here apply to future invoices only — existing invoices are frozen.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="GST Number" field="gst_number" />
              <Field label="Payment Terms" field="payment_terms" placeholder="e.g. Due within 14 days" />
              <Field label="Bank Name" field="payment_bank_name" placeholder="e.g. ANZ" />
              <Field label="Account Name" field="payment_account_name" placeholder="e.g. Roofs To Remedy Ltd" />
              <Field label="Account Number" field="payment_account_number" placeholder="e.g. 01-0123-0123456-00" />
            </div>
            <Field label="Payment Instructions" field="payment_instructions" rows={3} placeholder="e.g. Please use invoice number as reference when making payment." />
            <Field label="Invoice Footer Text" field="invoice_footer_text" rows={2} placeholder="e.g. Thank you for choosing Roofs To Remedy for your roofing needs." />
            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] hover:bg-[#A04000] text-white'} disabled:opacity-50`}>
                <Save size={14} /> {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Invoice Settings'}
              </button>
            </div>
          </div>
        )}

        {tab === 'gst' && (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">GST Filing Settings</h3>
              <p className="text-sm text-slate-500">Configure your GST filing period. These settings are used as defaults for the Financial Dashboard GST Report and GST101A form generation.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">GST Number</label>
                  <input data-testid="gst-number-input" value={form.gst_number || ''} onChange={e => u('gst_number', e.target.value)} placeholder="e.g. 123-456-789" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">GST Basis</label>
                  <select data-testid="gst-basis-select" value={form.gst_basis || 'invoice'} onChange={e => u('gst_basis', e.target.value)} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    <option value="invoice">Invoice Basis</option>
                    <option value="payments">Payments Basis</option>
                  </select>
                  <p className="text-[10px] text-slate-400 mt-1">Invoice basis: GST on invoice date. Payments basis: GST when paid.</p>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Filing Frequency</label>
                  <select data-testid="gst-filing-frequency-select" value={form.gst_filing_frequency || 2} onChange={e => u('gst_filing_frequency', parseInt(e.target.value))} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    <option value={1}>Monthly</option>
                    <option value={2}>2-Monthly</option>
                    <option value={6}>6-Monthly</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Period Start Month</label>
                  <select data-testid="gst-period-start-select" value={form.gst_period_start_month || 1} onChange={e => u('gst_period_start_month', parseInt(e.target.value))} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm bg-white">
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                  </select>
                  <p className="text-[10px] text-slate-400 mt-1">First month of your GST filing cycle</p>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button data-testid="save-gst-settings-btn" onClick={save} disabled={saving} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] hover:bg-[#A04000] text-white'} disabled:opacity-50`}>
                  <Save size={14} /> {saving ? 'Saving...' : saved ? 'Saved!' : 'Save GST Settings'}
                </button>
              </div>
            </div>

            <div className="border-t border-[#E2E2DF] pt-5">
              <h3 className="text-lg font-semibold text-slate-800 mb-1">GST Rate History</h3>
              <p className="text-sm text-slate-500">The system applies the GST rate that was effective on the appraisal date. Add a new rate with a future date to apply it to future quotes automatically. Past appraisals retain their original rate.</p>
            </div>

            {/* Add new rate */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Add GST Rate Change</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Rate (%)</label>
                  <input data-testid="gst-rate-input" type="number" step="0.1" value={newGst.rate} onChange={e => setNewGst(g => ({ ...g, rate: e.target.value }))} placeholder="e.g. 15" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Effective From</label>
                  <input data-testid="gst-date-input" type="date" value={newGst.effective_from} onChange={e => setNewGst(g => ({ ...g, effective_from: e.target.value }))} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">Description</label>
                  <input value={newGst.description} onChange={e => setNewGst(g => ({ ...g, description: e.target.value }))} placeholder="e.g. Government rate change" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm" />
                </div>
                <button data-testid="add-gst-rate-btn" onClick={addGstRate} disabled={!newGst.rate || !newGst.effective_from} className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-md text-sm font-semibold hover:bg-[#A04000] disabled:opacity-40">
                  <Plus size={14} /> Add Rate
                </button>
              </div>
            </div>

            {/* Rates table */}
            <div className="border border-[#E2E2DF] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-[#E2E2DF]">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500">GST Rate</th>
                    <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500">Effective From</th>
                    <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-500">Description</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {gstRates.map((r, idx) => (
                    <tr key={r._id} className={`border-b border-[#E2E2DF] last:border-0 ${idx === 0 ? 'bg-green-50' : ''}`}>
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{r.rate}%{idx === 0 && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">CURRENT</span>}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.effective_from ? (() => { try { const [y,m,d] = r.effective_from.split('-'); return `${d}/${m}/${y}`; } catch { return r.effective_from; } })() : '-'}</td>
                      <td className="px-4 py-2.5 text-slate-500">{r.description || '-'}</td>
                      <td className="px-4 py-2.5">
                        {gstRates.length > 1 && <button onClick={() => deleteGstRate(r._id)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>}
                      </td>
                    </tr>
                  ))}
                  {gstRates.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-6 text-slate-400">No GST rates configured. Add one above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function TemplateEditor({ template, onSave, onCancel }) {
  const [form, setForm] = useState(template);

  const toggleSection = (key) => {
    setForm(f => ({
      ...f,
      sections: (f.sections || []).includes(key) ? f.sections.filter(s => s !== key) : [...(f.sections || []), key]
    }));
  };

  return (
    <div data-testid="template-editor" className="border-2 border-[#D35400] rounded-xl p-5 space-y-4 bg-orange-50/20">
      <h3 className="text-sm font-bold text-[#2C2C2C]">{template._id ? 'Edit Template' : 'New Template'}</h3>
      <div>
        <label className="text-xs font-medium text-slate-500 block mb-1">Quote Type *</label>
        <input data-testid="template-quote-type" value={form.quote_type || ''} onChange={e => setForm({...form, quote_type: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" placeholder="e.g. Remedial Repairs" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-500 block mb-1.5">Report Sections (toggle on/off)</label>
        <div className="flex flex-wrap gap-2">
          {ALL_SECTIONS.map(s => (
            <button key={s.key} onClick={() => toggleSection(s.key)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${(form.sections || []).includes(s.key) ? 'bg-[#D35400] text-white border-[#D35400]' : 'border-[#E2E2DF] text-slate-500 hover:border-[#D35400]'}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-500 block mb-1">Intro Override (blank = use default)</label>
          <textarea value={form.intro_override || ''} onChange={e => setForm({...form, intro_override: e.target.value})} rows={3} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
        </div>
        <div className="space-y-3 pt-5">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.show_restoration_estimate ?? true} onChange={e => setForm({...form, show_restoration_estimate: e.target.checked})} /> Show Restoration Estimate</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.show_reroof_estimate ?? true} onChange={e => setForm({...form, show_reroof_estimate: e.target.checked})} /> Show Re-Roof Estimate</label>
        </div>
      </div>
      <div className="flex gap-2">
        <button data-testid="save-template-btn" onClick={() => onSave(form)} disabled={!form.quote_type?.trim()} className="px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] disabled:opacity-50">Save Template</button>
        <button onClick={onCancel} className="px-4 py-2 border border-[#E2E2DF] rounded-lg text-sm hover:bg-slate-50">Cancel</button>
      </div>
    </div>
  );
}


/**
 * iter297d — Section Spacing editor.
 * Live-tunes `report_settings.variant_section_gaps.{variant}` (mm) —
 * the extra whitespace inserted between every emitted script cell for
 * a given draft type. Kept tiny (0-8mm) so mis-configured values can't
 * push content onto a second page.
 */
const VARIANT_META = [
  { key: 'instant_free',    label: 'Instant Free',       hint: '1-page free appraisal — leave low (0-0.5mm) so content stays on page 1' },
  { key: 'detailed_single', label: 'Detailed 1-Comment', hint: '2-page detailed report — 0.5-1.5mm reads well' },
  { key: 'detailed_multi',  label: 'Detailed Multi',     hint: '3+ pages with photos — small gaps only (0-0.5mm)' },
];
const DEFAULT_GAPS = { instant_free: 0.3, detailed_single: 0.5, detailed_multi: 0.5 };

function SectionSpacingEditor({ form, u, save, saving, saved }) {
  const gaps = form.variant_section_gaps || {};
  const setGap = (variant, value) => {
    const next = { ...gaps, [variant]: Number(value) };
    u('variant_section_gaps', next);
  };

  return (
    <div className="space-y-5" data-testid="section-spacing-editor">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-1">Section Spacing</h3>
        <p className="text-sm text-slate-500">Extra whitespace (in millimetres) inserted <strong>between every script cell</strong> for each draft type. Keeps the page visually balanced without a two-pass measurement at each render. Hard-capped at 8mm to prevent accidental page-overflow.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
        <strong>Tip:</strong> Start small (0.3-0.5mm) and increase in 0.1mm steps until the layout feels right. If content jumps to a second page, dial back by 0.1mm.
      </div>

      <div className="space-y-4">
        {VARIANT_META.map(v => {
          const val = gaps[v.key] ?? DEFAULT_GAPS[v.key];
          return (
            <div key={v.key} className="border border-[#E2E2DF] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">{v.label}</h4>
                  <p className="text-[11px] text-slate-500">{v.hint}</p>
                </div>
                <div className="text-xs font-mono text-[#316536] font-bold bg-green-50 px-2.5 py-1 rounded" data-testid={`gap-value-${v.key}`}>
                  {val.toFixed(1)} mm
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 font-mono w-8">0.0</span>
                <input
                  data-testid={`gap-slider-${v.key}`}
                  type="range"
                  min={0}
                  max={8}
                  step={0.1}
                  value={val}
                  onChange={e => setGap(v.key, e.target.value)}
                  className="flex-1 accent-[#316536]"
                />
                <span className="text-[10px] text-slate-400 font-mono w-8 text-right">8.0</span>
                <input
                  data-testid={`gap-input-${v.key}`}
                  type="number"
                  min={0}
                  max={8}
                  step={0.1}
                  value={val}
                  onChange={e => setGap(v.key, e.target.value)}
                  className="w-20 px-2 py-1 border border-[#E2E2DF] rounded text-sm font-mono text-right"
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          data-testid="save-section-spacing"
          onClick={save}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] hover:bg-[#A04000] text-white'} disabled:opacity-50`}
        >
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Section Spacing'}
        </button>
      </div>
    </div>
  );
}


/**
 * iter297h — Roof Rates editor.
 * Single table with 6 rows (one per roof type) × 2 rate columns:
 *   • Restoration ($/m²) — writes to estimate_templates.estimate_rows[key=restoration].rate
 *     across ALL 3 variants (instant_free / detailed_single / detailed_multi)
 *   • Re-Roof ($/m²) — writes to reroof_rate_card (target = longrun_iron)
 *
 * Rates are used by the PDF renderer:
 *   Restoration Estimate = rate × roof_m² (rounded to nearest $100)
 *   Re-Roof Estimate     = rate × roof_m² (rounded to nearest $100)
 */
const ROOF_META = [
  { key: 'longrun_iron',    label: 'Longrun Iron'    },
  { key: 'color_tile',      label: 'Color Tile'      },
  { key: 'decramastic',     label: 'Decramastic'     },
  { key: 'modern_concrete', label: 'Modern Concrete' },
  { key: 'old_concrete',    label: 'Old Concrete'    },
  { key: 'clay_tile',       label: 'Clay Tile'       },
];

function RoofRatesEditor({ api }) {
  const [restoration, setRestoration] = useState({});
  const [reroof, setReroof] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/script-audit/roof-rates');
      setRestoration(data.restoration || {});
      setReroof(data.reroof || {});
      setDirty(false);
    } catch (e) {
      alert('Failed to load roof rates: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const setResto = (rk, val) => { setRestoration(r => ({ ...r, [rk]: Number(val) })); setDirty(true); setSaved(false); };
  const setReRo  = (rk, val) => { setReroof(r =>       ({ ...r, [rk]: Number(val) })); setDirty(true); setSaved(false); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/script-audit/roof-rates', { restoration, reroof });
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  if (loading) return <div className="text-sm text-slate-500 py-6 text-center">Loading rates…</div>;

  return (
    <div className="space-y-5" data-testid="roof-rates-editor">
      <div>
        <h3 className="text-lg font-semibold text-slate-800 mb-1">Roof Rates ($/m²)</h3>
        <p className="text-sm text-slate-500">Per-roof-type rates used by the PDF renderer for the <strong>Restoration Estimate</strong> and <strong>Re-Roof Estimate</strong> lines. Both formulas: <code className="text-xs bg-slate-100 px-1 rounded">rate × roof_m²</code>, rounded to nearest $100.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
        <strong>Note:</strong> Restoration rate edits are applied across all 3 variants (Instant Free · Detailed Single · Detailed Multi) for each roof type. Re-Roof rates target Longrun Iron as the &quot;re-roof to&quot; material.
      </div>

      <div className="border border-[#E2E2DF] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold text-slate-700">Roof Type</th>
              <th className="px-4 py-2.5 text-right font-semibold text-slate-700 w-40">Restoration $/m²</th>
              <th className="px-4 py-2.5 text-right font-semibold text-slate-700 w-40">Re-Roof $/m²</th>
            </tr>
          </thead>
          <tbody>
            {ROOF_META.map(rf => (
              <tr key={rf.key} className="border-t border-[#E2E2DF] hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium text-slate-800">{rf.label}</td>
                <td className="px-4 py-2 text-right">
                  <input
                    data-testid={`resto-rate-${rf.key}`}
                    type="number" min={0} max={1000} step={0.5}
                    value={restoration[rf.key] ?? 0}
                    onChange={e => setResto(rf.key, e.target.value)}
                    className="w-28 px-2 py-1 border border-[#E2E2DF] rounded text-right font-mono focus:border-[#316536] focus:outline-none focus:ring-1 focus:ring-[#316536]"
                  />
                </td>
                <td className="px-4 py-2 text-right">
                  <input
                    data-testid={`reroof-rate-${rf.key}`}
                    type="number" min={0} max={1000} step={1}
                    value={reroof[rf.key] ?? 0}
                    onChange={e => setReRo(rf.key, e.target.value)}
                    className="w-28 px-2 py-1 border border-[#E2E2DF] rounded text-right font-mono focus:border-[#316536] focus:outline-none focus:ring-1 focus:ring-[#316536]"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <button
          data-testid="reload-roof-rates"
          onClick={load}
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-slate-700 border border-[#E2E2DF] rounded hover:bg-slate-50 disabled:opacity-50"
        >Reload</button>
        <button
          data-testid="save-roof-rates"
          onClick={save}
          disabled={saving || !dirty}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : dirty ? 'bg-[#D35400] hover:bg-[#A04000] text-white' : 'bg-slate-300 text-slate-500 cursor-not-allowed'}`}
        >
          {saving ? 'Saving...' : saved ? '✓ Saved!' : dirty ? '💾 Save Rates' : 'No changes'}
        </button>
      </div>

      {dirty && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          ● You have unsaved changes. Click <strong>Save Rates</strong> to persist. Rates take effect on the next PDF render.
        </div>
      )}
    </div>
  );
}
